import axios from 'axios';
import { API_BASE_URL } from '../config/api.config';

const API_URL = `${API_BASE_URL}/pdf-to-json/sharepoint`;

export interface SharePointSite {
  id: string;
  displayName?: string;
  name: string;
  webUrl: string;
  createdDateTime?: string;
  lastModifiedDateTime?: string;
}

export interface DocumentLibrary {
  id: string;
  name: string;
  description?: string;
  driveType?: string;
  createdDateTime?: string;
  lastModifiedDateTime?: string;
}

export interface DriveItem {
  id: string;
  name: string;
  folder?: { childCount: number };
  file?: { mimeType: string };
  size?: number;
  webUrl: string;
  createdDateTime: string;
  lastModifiedDateTime: string;
  parentReference?: {
    driveId: string;
    id: string;
    path: string;
  };
}

export interface Breadcrumb {
  name: string;
  id: string | null;
}


class SharePointSyncService {
  /**
   * Get all SharePoint sites
   */
  async getSites(): Promise<SharePointSite[]> {
    try {
      const response = await axios.get(`${API_URL}/sites`);
      return response.data.data || [];
    } catch (error: any) {
      console.error('[SharePointSyncService] Error fetching sites:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch SharePoint sites');
    }
  }

  /**
   * Save file to Pinecone (RAG)
   */
  async saveToRAG(driveId: string, fileId: string, fileName: string, mimeType: string, webUrl: string): Promise<any> {
    try {
      const response = await axios.post(`${API_BASE_URL}/sharepoint-sync/save-to-rag`, {
        driveId,
        fileId,
        fileName,
        mimeType,
        webUrl
      });
      return response.data;
    } catch (error: any) {
      console.error('[SharePointSyncService] Error saving to RAG:', error);

      // Handle duplicate file case (409 status)
      if (error.response?.status === 409 || error.response?.data?.alreadySaved) {
        const err = new Error('File already exists in Pinecone database');
        (err as any).alreadySaved = true;
        throw err;
      }

      throw new Error(error.response?.data?.error || 'Failed to save file to RAG');
    }
  }

  /**
   * Check if file exists in RAG
   */
  async checkFileInRAG(fileId: string): Promise<boolean> {
    try {
      const response = await axios.get(`${API_BASE_URL}/sharepoint-sync/check/${fileId}`);
      return response.data.data?.existsInRAG || false;
    } catch (error: any) {
      console.error('[SharePointSyncService] Error checking file in RAG:', error);
      return false;
    }
  }

  /**
   * Get document libraries (drives) for a specific site
   */
  async getLibraries(siteId: string): Promise<DocumentLibrary[]> {
    try {
      const response = await axios.get(`${API_URL}/sites/${siteId}/drives`);
      return response.data.data?.drives || [];
    } catch (error: any) {
      console.error('[SharePointSyncService] Error fetching libraries:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch document libraries');
    }
  }

  /**
   * Get drive root items
   */
  async getDriveItems(driveId: string, folderId?: string): Promise<DriveItem[]> {
    try {
      let response;
      if (folderId) {
        response = await axios.get(`${API_URL}/drives/${driveId}/folder/${folderId}`);
      } else {
        response = await axios.get(`${API_URL}/drives/${driveId}`);
      }
      return response.data.data?.items || [];
    } catch (error: any) {
      console.error('[SharePointSyncService] Error fetching drive items:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch folder contents');
    }
  }

  /**
   * Get breadcrumb trail for current folder
   */
  async getBreadcrumbs(driveId: string, folderId?: string): Promise<Breadcrumb[]> {
    try {
      if (!folderId) return [];
      const response = await axios.get(`${API_URL}/drives/${driveId}/folder/${folderId}`);
      return response.data.data?.breadcrumbs || [];
    } catch (error: any) {
      console.error('[SharePointSyncService] Error fetching breadcrumbs:', error);
      return [];
    }
  }

  /**
   * Get download URL for a file
   */
  async getDownloadUrl(driveId: string, itemId: string): Promise<string> {
    try {
      const response = await axios.get(`${API_URL}/drives/${driveId}/download/${itemId}`);
      return response.data.data?.downloadUrl || '';
    } catch (error: any) {
      console.error('[SharePointSyncService] Error getting download URL:', error);
      throw new Error(error.response?.data?.error || 'Failed to get download link');
    }
  }

  /**
   * Download file content
   */
  async downloadFile(driveId: string, itemId: string): Promise<Blob> {
    try {
      const response = await axios.get(`${API_URL}/drives/${driveId}/file/${itemId}`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error: any) {
      console.error('[SharePointSyncService] Error downloading file:', error);
      throw new Error(error.response?.data?.error || 'Failed to download file');
    }
  }
}

export default new SharePointSyncService();
